/**
 * 판정 기록 상세 — 줄별 "당시 적용된 룰" 본문(D-21).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * 줄(pidx === item.index)의 ruleVersions마다 버전 문구 + 당시 룰 패널을 그린다.
 * 중앙 룰: versions API 응답 본문(rawContent 있음 → 거짓 "구버전" 배지 없음). 응답 ruleId가 요청과
 * 다르면 소속 검증 실패로 rules_json 폴백(09-25 결정 8). 약국 룰·versions API 오류: rules_json의
 * id === rowId 행. 모든 패널 suppressLegacyBadges(폴백·과거 버전 단위의 거짓 경고 억제, D-12).
 */
import React from "react";
import DoseRuleSegmentPanel from "components/organisms/DoseRuleSegmentPanel";
import { resolveVersionPrescribeUnit } from "utils/doseRule";
import type { DoseReviewItem } from "api/doseReviewService";
import type {
  DoseReviewLogRuleVersion,
  DoseReviewLogAppliedRuleRow,
} from "services/api/doseReviewLogs/types";
import type {
  AdminDoseRuleVersionDetail,
  DoseRuleContent,
} from "services/api/doseRules/types";
import { VersionLine } from "./detailStyle";

/** 버전 상세 쿼리 결과(필요한 것만) — rowId로 조회. */
export interface VersionDetailState {
  data?: AdminDoseRuleVersionDetail;
  isLoading: boolean;
  isError: boolean;
}

export interface RuleVersionBlockProps {
  item: DoseReviewItem;
  ruleVersions: DoseReviewLogRuleVersion[];
  rules: DoseReviewLogAppliedRuleRow[] | null;
  versionDetails: Map<number, VersionDetailState>;
}

interface PanelProps {
  content: DoseRuleContent;
  rawContent: DoseRuleContent | null;
  criteriaVersion: string | null;
  isDerived: boolean;
  prescribeUnit: string | null;
}

const readCriteriaVersion = (prov: Record<string, unknown> | null | undefined): string | null => {
  const v = prov?.criteria_version;
  return typeof v === "string" ? v : null;
};

const fromVersionDetail = (d: AdminDoseRuleVersionDetail): PanelProps | null => {
  if (!d.content) return null;
  return {
    content: d.content,
    rawContent: d.rawContent,
    criteriaVersion: readCriteriaVersion(d.provenance),
    isDerived: !!d.derivedFrom,
    prescribeUnit: resolveVersionPrescribeUnit(d.productRef),
  };
};

const fromRulesRow = (r: DoseReviewLogAppliedRuleRow | undefined): PanelProps | null => {
  if (!r || !r.content) return null;
  return {
    content: r.content,
    rawContent: null, // rules_json 행엔 rawContent가 없다
    criteriaVersion: readCriteriaVersion(r.provenance),
    isDerived: !!r.derivedFrom,
    prescribeUnit: resolveVersionPrescribeUnit(r.productRef),
  };
};

const versionText = (rv: DoseReviewLogRuleVersion): string => {
  if (rv.version == null) return "버전 정보 없음";
  const base = `그때 적용된 룰 v${rv.version}`;
  if (rv.currentVersion != null && rv.currentVersion !== rv.version) {
    return `${base} · 현재는 v${rv.currentVersion}`;
  }
  return base;
};

const RuleVersionBlock: React.FC<RuleVersionBlockProps> = ({
  item,
  ruleVersions,
  rules,
  versionDetails,
}) => {
  const lineVersions = ruleVersions.filter((rv) => rv.pidx === item.index);
  if (lineVersions.length === 0) return null;

  const rulesRow = (rowId: number) => (rules ?? []).find((r) => r.id === rowId);

  return (
    <div data-testid="dose-rule-version-block">
      {lineVersions.map((rv) => {
        let panel: PanelProps | null = null;

        if (!rv.isPharmacyRule) {
          const q = versionDetails.get(rv.rowId);
          if (q?.isLoading) {
            return (
              <div key={rv.rowId}>
                <VersionLine>{versionText(rv)}</VersionLine>
                <VersionLine data-testid="dose-version-loading">당시 룰 불러오는 중…</VersionLine>
              </div>
            );
          }
          // 소속 검증(09-25 결정 8): 응답 ruleId가 요청 줄의 ruleId와 같아야 본문을 쓴다.
          if (q?.data && !q.isError && q.data.ruleId === rv.ruleId) {
            panel = fromVersionDetail(q.data);
          } else {
            panel = fromRulesRow(rulesRow(rv.rowId)); // 오류·없음·소속 불일치 → rules_json 폴백
          }
        } else {
          // 약국 룰(P-룰): versions API가 못 찾으므로 rules_json 행으로.
          panel = fromRulesRow(rulesRow(rv.rowId));
        }

        return (
          <div key={rv.rowId}>
            <VersionLine data-testid="dose-version-line">{versionText(rv)}</VersionLine>
            {panel && (
              <DoseRuleSegmentPanel
                content={panel.content}
                rawContent={panel.rawContent}
                criteriaVersion={panel.criteriaVersion}
                isDerived={panel.isDerived}
                prescribeUnit={panel.prescribeUnit}
                perUnit={null}
                suppressLegacyBadges
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RuleVersionBlock;
